import { useEffect, type ReactNode } from "react"
import { create } from "zustand"

export type InspectorTab = {
  id: string
  label: string
  icon?: ReactNode
}

/**
 * A store, not context: `<Module>` renders the inspector as a SIBLING of the page, so no provider
 * around the page reaches it. The page registers here; the inspector chrome subscribes.
 */
type InspectorTabStore = {
  tabs: readonly InspectorTab[]
  activeTabId: string | null
  primaryTabId: string | null
  arrowKeys: boolean
  /** A deeplink's pending tab, stamped with the primary it was requested against. */
  request: { tabId: string; from: string | null } | null
  register: (tabs: readonly InspectorTab[], primaryTabId: string, arrowKeys: boolean) => void
  unregister: () => void
  setActiveTabId: (id: string, request?: { tabId: string; from: string | null }) => void
}

const NO_TABS: readonly InspectorTab[] = []

const useInspectorTabStore = create<InspectorTabStore>((set) => ({
  tabs: NO_TABS,
  activeTabId: null,
  primaryTabId: null,
  arrowKeys: false,
  request: null,
  // Resets to the primary only when the primary changes. A deeplink's `request` carries the primary
  // it was made against: honoured by the registration that completes it, then dropped either way.
  register: (tabs, primaryTabId, arrowKeys) =>
    set((state) => {
      const request = state.request
      const honours = request != null && request.from !== primaryTabId
      if (state.primaryTabId === primaryTabId && state.tabs === tabs) {
        if (request == null && state.arrowKeys === arrowKeys) return state
        return { ...state, arrowKeys, request: null }
      }
      const samePrimary = state.primaryTabId === primaryTabId
      return {
        tabs,
        primaryTabId,
        arrowKeys,
        activeTabId: honours ? request.tabId : samePrimary ? state.activeTabId : primaryTabId,
        request: null,
      }
    }),
  unregister: () =>
    set({ tabs: NO_TABS, activeTabId: null, primaryTabId: null, arrowKeys: false, request: null }),
  // A planner's own pick cancels any pending deeplink.
  setActiveTabId: (id, request) => set({ activeTabId: id, request: request ?? null }),
}))

/**
 * Declares the inspector's tabs and which one this page leads with; the chrome draws the row above
 * the views. Switching page resets to its `primaryTabId`. Pass `null` while there are no tabs.
 * `tabs` must be a stable array: registration keys off its identity.
 */
export const useInspectorTab = (
  tabs: readonly InspectorTab[],
  primaryTabId: string | null,
  options?: {
    arrowKeys?: boolean
  },
): void => {
  const arrowKeys = options?.arrowKeys ?? false
  const register = useInspectorTabStore((s) => s.register)
  const unregister = useInspectorTabStore((s) => s.unregister)

  useEffect(() => {
    if (primaryTabId === null) {
      unregister()
      return
    }
    register(tabs, primaryTabId, arrowKeys)
  }, [tabs, primaryTabId, arrowKeys, register, unregister])

  useEffect(() => unregister, [unregister])
}

/** The primary stands in before registration, or every tab's content flashes hidden. */
const selectActiveTabId = (s: InspectorTabStore) => s.activeTabId ?? s.primaryTabId

export const useIsInspectorTabActive = (tabId: string) =>
  useInspectorTabStore(selectActiveTabId) === tabId

/** The tab row's own state. Read by the inspector chrome; features use {@link useInspectorTab}. */
export const useInspectorTabBar = () => {
  const tabs = useInspectorTabStore((s) => s.tabs)
  const activeTabId = useInspectorTabStore(selectActiveTabId)
  const setActiveTabId = useInspectorTabStore((s) => s.setActiveTabId)
  const arrowKeys = useInspectorTabStore((s) => s.arrowKeys)
  return { tabs, activeTabId, setActiveTabId, arrowKeys }
}

/**
 * Moves the inspector to a tab from outside it (a deeplink's last step). Stable identity, so it is
 * safe inside a memoised grid column.
 */
export const setInspectorTab = (tabId: string) => {
  const { primaryTabId, setActiveTabId } = useInspectorTabStore.getState()
  // Stamped with the current primary: only a registration for a DIFFERENT page honours it.
  setActiveTabId(tabId, { tabId, from: primaryTabId })
}
